import { Injectable } from '@angular/core';
import { PlaylistTrack, Track } from '@app/core/models/Track';
import { RadioState } from '@app/core/states/radio.state';
import {
  AddToPlaylistSuccess,
  UpdatePlaylistTracksCountAfterDelete,
} from '@app/library/states/playlists.actions';
import { PlaylistsState } from '@app/library/states/playlists.state';
import {
  TrackArchiveMultiBacSuccess,
  TrackArchiveSuccess,
  TrackCoverDeleteSuccess,
  TrackCoverLookupSuccess,
  TrackCoverMultipleSetSuccess,
  TrackCoverSingleSetSuccess,
  TracksUploaded,
  TrackTagsSaveSuccess,
  UpdateTrackFilter,
  UpdateTracksFilter,
} from '@app/library/states/tracks.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { PLACEHOLDER } from '@app/shared/constants';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { compose, patch } from '@ngxs/store/operators';
import { patchKey, updateMultipleItems } from '@radioking/xs-state-operators';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';

import { PlaylistService } from '../services/playlist.service';

import {
  DeletePlaylistTracksFailure,
  DeletePlaylistTracksRequest,
  DeletePlaylistTracksSuccess,
  MovePlaylistTracksFailure,
  MovePlaylistTracksRequest,
  MovePlaylistTracksSuccess,
  PlaylistTracksFailure,
  PlaylistTracksRequest,
  PlaylistTracksRequestNoLoader,
  PlaylistTracksSuccess,
} from './playlist-tracks.actions';

export class PlaylistTracksStateModel {
  playlist: {
    [key: string]: PlaylistTrack[];
  };
  isFetching: boolean;
  currentPlaylist: number;
}

@State<PlaylistTracksStateModel>({
  name: 'playlistTracks',
  defaults: {
    playlist: {},
    isFetching: false,
    currentPlaylist: 0,
  },
})
@Injectable()
export class PlaylistTracksState {
  constructor(
    private readonly store: Store,
    private readonly playlistService: PlaylistService,
  ) {}

  @Selector()
  static playlistTracks(
    state: PlaylistTracksStateModel,
  ): (idPlaylist: number) => PlaylistTrack[] {
    return idPlaylist => {
      const key = `playlist${idPlaylist}`;

      return state.playlist[key];
    };
  }

  @Selector()
  static currentPlaylist(state: PlaylistTracksStateModel): number {
    return state.currentPlaylist;
  }

  @Selector()
  static playlistTracksTime(state: PlaylistTracksStateModel) {
    return (idPlaylist: number): number => {
      const key = `playlist${idPlaylist}`;

      return state.playlist && state.playlist[key]
        ? state.playlist[key].reduce(
            (pre: number, current: Track) => pre + (current.timeSeconds || 0),
            0,
          )
        : 0;
    };
  }

  @Selector([PlaylistsState.playlistProcessing])
  static playlistCoversPreview(
    state: PlaylistTracksStateModel,
    isProcessingFn: (idPlaylist: number) => boolean,
  ): (idPlaylist: number) => string[] {
    return (idPlaylist: number): string[] => {
      const isProcessing = isProcessingFn(idPlaylist);
      if (isProcessing) {
        return Array(4).fill(PLACEHOLDER);
      }
      const key = `playlist${idPlaylist}`;
      const playlist = state.playlist[key];
      const covers = [];
      if (playlist && playlist.length) {
        for (let i = 0; i < 4; i = i + 1) {
          covers[i] = playlist.length > i ? playlist[i].cover : playlist[0].cover;
        }

        return covers;
      }

      return Array(4).fill(PLACEHOLDER);
    };
  }

  @Action([PlaylistTracksRequest, PlaylistTracksRequestNoLoader], {
    cancelUncompleted: true,
  })
  getPlaylistTracks(
    ctx: StateContext<PlaylistTracksStateModel>,
    { idPlaylist }: PlaylistTracksRequest | PlaylistTracksRequestNoLoader,
  ) {
    ctx.patchState({ isFetching: true, currentPlaylist: idPlaylist });

    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.playlistService.getPlaylistTracks(id, idPlaylist)),
      mergeMap(data => ctx.dispatch(new PlaylistTracksSuccess(idPlaylist, data))),
      catchError(err => ctx.dispatch(new PlaylistTracksFailure(err))),
    );
  }

  @Action(PlaylistTracksSuccess)
  getPlaylistTracksSuccess(
    ctx: StateContext<PlaylistTracksStateModel>,
    { idPlaylist, tracks }: PlaylistTracksSuccess,
  ) {
    const key = `playlist${idPlaylist}`;
    ctx.setState(
      patch<PlaylistTracksStateModel>({
        playlist: patchKey<PlaylistTrack>(key, tracks),
        isFetching: false,
        currentPlaylist: idPlaylist,
      }),
    );
  }

  @Action(AddToPlaylistSuccess)
  addToPlaylistSuccess(
    ctx: StateContext<PlaylistTracksStateModel>,
    { playlistId, tracks }: AddToPlaylistSuccess,
  ) {
    const key = `playlist${playlistId}`;
    ctx.setState(
      patch({
        playlist: patchKey(key, tracks),
      }),
    );
  }

  @Action(TracksUploaded)
  addUploadedTrack(
    ctx: StateContext<PlaylistTracksStateModel>,
    { playlist, track }: TracksUploaded,
  ) {
    if (!playlist) {
      return;
    }

    const key = `playlist${playlist.id}`;

    if (!ctx.getState().playlist[key]) {
      return;
    }

    const playlistTrack: PlaylistTrack = {
      idPlaylist: playlist.id,
      position: playlist.nbTracks + 1,
      ...track,
      addedToLibraryDate: moment(),
    };
    const currentPlaylistTracks: PlaylistTrack[] = [
      ...ctx.getState().playlist[key],
      playlistTrack,
    ];
    ctx.dispatch(
      new AddToPlaylistSuccess(
        playlist.id,
        currentPlaylistTracks,
        { count: 1 },
        currentPlaylistTracks.length,
      ),
    );
  }

  @Action(UpdateTrackFilter)
  updateTrackFilter(
    ctx: StateContext<PlaylistTracksStateModel>,
    { track }: UpdateTrackFilter,
  ) {
    const changes = Object.keys(ctx.getState().playlist).map(key =>
      patch<PlaylistTracksStateModel>({
        playlist: patch({
          [key]: updateMultipleItems<PlaylistTrack>(
            pTrack => pTrack.id === track.id,
            patch({ ...track }),
          ),
        }),
      }),
    );
    ctx.setState(compose(...changes));
  }

  @Action(UpdateTracksFilter)
  updateTracksFilter(
    ctx: StateContext<PlaylistTracksStateModel>,
    { tracks }: UpdateTracksFilter,
  ) {
    const tracksIdsMap = new Map(tracks.map(t => [t.track.id, t]));
    const operators = Object.keys(ctx.getState().playlist).map(key =>
      patch<PlaylistTracksStateModel>({
        playlist: patch({
          [key]: updateMultipleItems<PlaylistTrack>(
            pTrack => tracksIdsMap.has(pTrack.id),
            (t: PlaylistTrack) => ({ ...t, ...tracksIdsMap.get(t.id).track }),
          ),
        }),
      }),
    );

    ctx.setState(compose(...operators));
  }

  @Action(TrackTagsSaveSuccess)
  updateTrackTags(
    ctx: StateContext<PlaylistTracksStateModel>,
    { id, tags }: TrackTagsSaveSuccess,
  ) {
    const changes = Object.keys(ctx.getState().playlist).map(key =>
      patch<PlaylistTracksStateModel>({
        playlist: patch({
          [key]: updateMultipleItems<PlaylistTrack>(
            pTrack => pTrack.id === id,
            patch({ tags }),
          ),
        }),
      }),
    );
    ctx.setState(compose(...changes));
  }

  @Action(PlaylistTracksFailure)
  getPlaylistTracksFailure(ctx: StateContext<PlaylistTracksStateModel>) {
    ctx.patchState({ isFetching: false });
  }

  @Action(MovePlaylistTracksRequest)
  movePlaylistTracks(
    ctx: StateContext<PlaylistTracksStateModel>,
    { idPlaylist, destPosition, tracksPosition }: MovePlaylistTracksRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id =>
        this.playlistService.movePlaylistTracks(
          id,
          idPlaylist,
          destPosition,
          tracksPosition,
        ),
      ),
      mergeMap(tracks => ctx.dispatch(new MovePlaylistTracksSuccess(idPlaylist, tracks))),
      catchError(err => ctx.dispatch(new MovePlaylistTracksFailure(err))),
    );
  }

  @Action(MovePlaylistTracksSuccess)
  movePlaylistTracksSuccess(
    ctx: StateContext<PlaylistTracksStateModel>,
    { idPlaylist, tracks }: MovePlaylistTracksSuccess,
  ) {
    const key = `playlist${idPlaylist}`;
    ctx.setState(
      patch<PlaylistTracksStateModel>({
        playlist: patchKey(key, tracks),
        isFetching: false,
      }),
    );
  }

  @Action(DeletePlaylistTracksRequest)
  deletePlaylistTrackRequest(
    ctx: StateContext<PlaylistTracksStateModel>,
    { idPlaylist, tracks }: DeletePlaylistTracksRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id =>
        this.playlistService.deletePlaylistTracks(
          id,
          idPlaylist,
          tracks.map(track => track.position),
        ),
      ),
      mergeMap(fromApi =>
        ctx.dispatch(new DeletePlaylistTracksSuccess(idPlaylist, fromApi)),
      ),
      catchError(err => ctx.dispatch(new DeletePlaylistTracksFailure(err))),
    );
  }

  @Action(DeletePlaylistTracksSuccess)
  deletePlaylistTrackSuccess(
    ctx: StateContext<PlaylistTracksStateModel>,
    { tracks, idPlaylist }: DeletePlaylistTracksSuccess,
  ) {
    const key = `playlist${idPlaylist}`;
    ctx.setState(
      patch({
        playlist: patchKey(key, tracks),
      }),
    );
  }

  @Action([TrackArchiveSuccess, TrackArchiveMultiBacSuccess])
  archiveTrackSuccess(
    ctx: StateContext<PlaylistTracksStateModel>,
    { tracksId }: TrackArchiveSuccess | TrackArchiveMultiBacSuccess,
  ) {
    const playlistList = this.store.selectSnapshot(PlaylistsState.playlists);
    let cachedTracks: Track[];

    // Remove tracks from playlists
    playlistList.forEach(playlist => {
      const key = `playlist${playlist.id}`;

      if (ctx.getState().playlist[key]) {
        const playlistTracks = ctx
          .getState()
          .playlist[key].filter(tr => !tracksId.includes(tr.id));
        ctx.setState(
          patch<PlaylistTracksStateModel>({
            playlist: patch({
              [key]: playlistTracks,
            }),
          }),
        );
        const tracksToBeRemoved = ctx.getState().playlist[key].length;
        this.store.dispatch(
          new UpdatePlaylistTracksCountAfterDelete(playlist.id, tracksToBeRemoved),
        );
      } else {
        /*
         Tracks of playlist are not fetched yet: process matching with Track.playlists
         (does not handle a track being more than one time in a playlist)
        */
        const trackWithId = this.store.selectSnapshot(TracksState.trackWithId);
        cachedTracks = cachedTracks ?? tracksId.map(id => trackWithId(id));
        const playlistTracks = cachedTracks.filter(t =>
          t?.playlists?.map(p => p.id)?.includes(playlist.id),
        );
        const tracksToBeRemoved = playlist.nbTracks - playlistTracks.length;
        this.store.dispatch(
          new UpdatePlaylistTracksCountAfterDelete(playlist.id, tracksToBeRemoved),
        );
      }
    });
  }

  @Action([TrackCoverSingleSetSuccess, TrackCoverLookupSuccess, TrackCoverDeleteSuccess])
  setCustomCover(
    ctx: StateContext<PlaylistTracksStateModel>,
    {
      imgUrl,
      track,
    }: TrackCoverSingleSetSuccess | TrackCoverLookupSuccess | TrackCoverDeleteSuccess,
  ) {
    const changes = Object.keys(ctx.getState().playlist).map(key =>
      patch<PlaylistTracksStateModel>({
        playlist: patch({
          [key]: updateMultipleItems<PlaylistTrack>(
            pTrack => pTrack.id === track.id,
            patch({ cover: imgUrl }),
          ),
        }),
      }),
    );
    ctx.setState(compose(...changes));
  }

  @Action(TrackCoverMultipleSetSuccess)
  setCustomCoverMultiple(
    ctx: StateContext<PlaylistTracksStateModel>,
    { tracksCover }: TrackCoverMultipleSetSuccess,
  ) {
    const coversByTrackId = new Map(tracksCover.map(tc => [tc.idtrack, tc.cover_url]));
    const changes = Object.keys(ctx.getState().playlist).map(key =>
      patch<PlaylistTracksStateModel>({
        playlist: patch({
          [key]: updateMultipleItems<PlaylistTrack>(
            pTrack => coversByTrackId.has(pTrack.id),
            (pTrack: PlaylistTrack) => ({
              ...pTrack,
              cover: coversByTrackId.get(pTrack.id),
            }),
          ),
        }),
      }),
    );
    ctx.setState(compose(...changes));
  }

  getCurrentRadioId(): Observable<number> {
    return this.store.select(RadioState.currentRadioId).pipe(
      filter(data => !!data),
      take(1),
    );
  }
}
